import getIncomeRanges from "@faire/web-api/api/user/income-ranges/get";
import { IDisplayableMoneyRange } from "@faire/web-api/indigofair/data/IDisplayableMoneyRange";
import { IIncomeRangesResponse } from "@faire/web-api/indigofair/data/IIncomeRangesResponse";
import { ISignUpRequest } from "@faire/web-api/indigofair/data/ISignUpRequest";
import { makeObservable } from "@faire/web/common/makeObservable";
import { moneyOrZero } from "@faire/web/common/money";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { IOption } from "@faire/web/slate/Input/Dropdown";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import keyBy from "lodash/keyBy";
import omit from "lodash/omit";
import { computed, observable } from "mobx";
import { lazyObservable } from "mobx-utils";

import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

/**
 * @deprecated prefer to use `useIncomeRangeStore` hook inside react components
 */
export class IncomeRangesStore {
  constructor() {
    makeObservable(this);
  }

  /**
   * @trackfunction
   */
  static get = singletonGetter(IncomeRangesStore);

  private country: string | undefined = undefined;

  private getIncomeRanges = async () => {
    const country = this.country ?? RetailerStore.get().retailerCountry;
    return await getIncomeRanges({ country });
  };

  @observable
  private lazyIncomeRanges = lazyObservable<IIncomeRangesResponse | undefined>(
    async (sink) => sink(await this.getIncomeRanges())
  );

  setIncomeRangesCountry = (country?: string) => {
    this.country = country;
  };

  refreshIncomeRanges = () => {
    if (this.lazyIncomeRanges.current() === undefined) {
      return;
    }
    this.lazyIncomeRanges.refresh();
  };

  @computed
  private get incomeRanges(): IDisplayableMoneyRange[] | undefined {
    return this.lazyIncomeRanges.current()?.income_money_ranges;
  }

  @computed
  get isLoading(): boolean {
    return this.lazyIncomeRanges.current() === undefined;
  }

  @computed
  get incomeRangesOptions(): IOption[] {
    if (!this.incomeRanges) {
      return [];
    }
    return this.incomeRanges.map((incomeRange: IDisplayableMoneyRange) => {
      return {
        label: incomeRange.display_text ?? "",
        value: incomeRange.display_text ?? "",
      };
    });
  }

  @computed
  get overOneMillionUSDSale(): boolean {
    const MIN_SALE_1M_INCOME_OPTION_INDEX = 5;
    return MoneyMath.greaterOrEqual(
      moneyOrZero(
        RetailerStore.get().retailer?.annual_sales_min_amount,
        RetailerStore.get().currency
      ),
      moneyOrZero(
        this.incomeRanges?.[MIN_SALE_1M_INCOME_OPTION_INDEX]?.min_amount,
        RetailerStore.get().currency
      )
    );
  }

  @computed
  private get incomeRangesByDisplayText() {
    return keyBy(this.incomeRanges, "display_text");
  }

  incomeRange = (displayText: string): Partial<ISignUpRequest.IAnnualSales> => {
    return omit(this.incomeRangesByDisplayText[displayText], "display_text");
  };
}

export const useIncomeRangesStore = createStoreHook(IncomeRangesStore);
